import * as XLSX from "xlsx-js-style";
import moment from "moment-timezone";
import { buildAuctionReport, noEndText, toNumber } from "./auctionReportData";

// same as the screens: 13/10/2026 04:12 PM (cells stay real dates)
const EXCEL_DATE_TIME_FORMAT = "dd/mm/yyyy hh:mm AM/PM";
const EXCEL_AMOUNT_FORMAT = "#,##0.00";

export const TITLE_STYLE = { font: { bold: true, sz: 14 } };
export const TABLE_HEADER_STYLE = {
  font: { bold: true },
  fill: { fgColor: { rgb: "D9E1FB" } },
  alignment: { vertical: "center", wrapText: true },
};
export const TOTAL_STYLE = { font: { bold: true } };
export const FOOTER_STYLE = {
  font: { italic: true, color: { rgb: "2D55EB" } },
};
export const WRAP_STYLE = { alignment: { wrapText: true, vertical: "top" } };

// Excel stores date-times as day counts with no time zone, so write the
// wall-clock time of the chosen zone. Excel then shows exactly that time.
export const dateCell = (value, zone, eventFormat) => {
  if (!value) return { t: "s", v: noEndText(eventFormat) };
  const local = moment(value).tz(zone);
  const wallClockMs = Date.UTC(
    local.year(),
    local.month(),
    local.date(),
    local.hour(),
    local.minute(),
    local.second()
  );
  return {
    t: "n",
    v: wallClockMs / 86400000 + 25569,
    z: EXCEL_DATE_TIME_FORMAT,
    s: { numFmt: EXCEL_DATE_TIME_FORMAT },
  };
};

export const amountCell = (value) => {
  const number = toNumber(value);
  if (number === null) return { t: "s", v: "" };
  return {
    t: "n",
    v: number,
    z: EXCEL_AMOUNT_FORMAT,
    s: { numFmt: EXCEL_AMOUNT_FORMAT },
  };
};

export const numberCell = (value) => {
  const number = toNumber(value);
  return number === null ? { t: "s", v: "" } : { t: "n", v: number };
};

export const textCell = (value) => ({
  t: "s",
  v: value === null || value === undefined ? "" : String(value),
});

export const withStyle = (cell, style) => ({
  ...cell,
  s: { ...(cell.s || {}), ...style },
});

const DEFAULT_COLUMN_WIDTH = 10;

// Groups the sheet's columns into boxes, left to right, each at least as wide
// as asked. The auction block uses them to sit neatly above the lot table,
// whose columns have different widths.
const columnSpans = (minWidths, columnWidths) => {
  let column = 0;
  return minWidths.map((minWidth) => {
    const first = column;
    let width = columnWidths[column] || DEFAULT_COLUMN_WIDTH;
    while (width < minWidth) {
      column += 1;
      width += columnWidths[column] || DEFAULT_COLUMN_WIDTH;
    }
    column += 1;
    return [first, column - 1];
  });
};

// Layout agreed from the user's draft:
//   Lot Owner / Auction / Description
//   a small summary table (type & ID, start, end, lots and bidders counts)
//   the lot table (if any)
//   Generated │ Powered by V3bid.com │ Created by
const buildSheet = ({ report, columnWidths, tableRows = [] }) => {
  const { timeZone, zoneAbbr, counts } = report;
  const rows = [];
  const merges = [];

  // cells[i] goes into spans[i]; null leaves the box empty. Boxes listed in
  // tallBoxes are merged with the row below as well.
  const putRow = (cells, spans, { style, tallBoxes = [] } = {}) => {
    const rowIndex = rows.length;
    const row = [];
    cells.forEach((cell, index) => {
      if (cell === null) return;
      const [first, last] = spans[index];
      row[first] = style ? withStyle(cell, style) : cell;
      const lastRow = tallBoxes.includes(index) ? rowIndex + 1 : rowIndex;
      if (last > first || lastRow > rowIndex) {
        merges.push({
          s: { r: rowIndex, c: first },
          e: { r: lastRow, c: last },
        });
      }
    });
    rows.push(row);
  };

  const labelSpans = columnSpans([13, 1], columnWidths);
  putRow(
    [
      withStyle(textCell("Lot Owner"), { font: { bold: true } }),
      withStyle(textCell(report.ownerOfLots), TITLE_STYLE),
    ],
    labelSpans
  );
  putRow(
    [
      withStyle(textCell("Auction:"), { font: { bold: true } }),
      withStyle(textCell(report.auctionTitle), {
        font: { bold: true, sz: 12 },
      }),
    ],
    labelSpans
  );
  if (report.description) {
    putRow(
      [
        withStyle(textCell("Description:"), { font: { bold: true } }),
        textCell(report.description),
      ],
      labelSpans
    );
  }
  rows.push([]);

  const summarySpans = columnSpans([26, 21, 21, 3, 17, 8, 8, 8], columnWidths);

  putRow(
    [
      "Auction Type & ID",
      `Start Date & Time (${zoneAbbr})`,
      `End Date & Time (${zoneAbbr})`,
      null,
      "",
      "Total",
      "Bid",
      "No bid",
    ].map((name) => (name === null ? null : textCell(name))),
    summarySpans,
    { style: TABLE_HEADER_STYLE }
  );
  putRow(
    [
      textCell(report.typeText),
      dateCell(report.startAt, timeZone),
      dateCell(report.endAt, timeZone, report.eventFormat),
      null,
      textCell("Lots"),
      numberCell(counts.lots),
      numberCell(counts.lotsWithBid),
      numberCell(counts.lotsNoBid),
    ],
    summarySpans,
    { tallBoxes: [1, 2] }
  );
  putRow(
    [
      textCell(report.eventNumber),
      null,
      null,
      null,
      textCell("Invited bidders"),
      numberCell(counts.invited),
      numberCell(counts.biddersWhoBid),
      numberCell(counts.invitedNoBid),
    ],
    summarySpans
  );
  rows.push([]);

  if (tableRows.length) {
    rows.push(...tableRows, []);
  }

  putRow(
    [
      textCell(`Generated: ${report.generatedText}`),
      withStyle(textCell("Powered by V3bid.com"), FOOTER_STYLE),
      textCell(`Created by: ${report.createdBy}`),
    ],
    columnSpans([30, 24, 1], columnWidths)
  );

  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  worksheet["!cols"] = columnWidths.map((wch) => ({ wch }));
  worksheet["!merges"] = merges;
  return worksheet;
};

const saveWorkbook = (worksheet, sheetName, fileName) => {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, fileName);
};

// Only the auction block: a lot table here would repeat the Lots file.
export const exportAuctionDetailsToXLSX = ({ eventData, lots, zone }) => {
  const report = buildAuctionReport({ eventData, lots, zone });
  const worksheet = buildSheet({
    report,
    columnWidths: [26, 21, 21, 3, 17, 8, 8, 8],
  });

  saveWorkbook(worksheet, "Auction", `${report.fileBase}-details.xlsx`);
};

const lotCell = (column, row, report) => {
  const value = row[column.key];
  switch (column.kind) {
    case "number":
      return numberCell(value);
    case "amount":
      return amountCell(value);
    case "date":
      return dateCell(value, report.timeZone, report.eventFormat);
    case "longText":
      return withStyle(textCell(value), WRAP_STYLE);
    default:
      return textCell(value);
  }
};

// Lot table column widths (characters)
const LOT_COLUMN_WIDTHS = {
  sr: 6,
  lotNumber: 10,
  description: 36,
  quantity: 8,
  unit: 8,
  startPrice: 15,
  step: 14,
  startAt: 21,
  endAt: 21,
  invited: 11,
  biddersBid: 8,
  bids: 11,
  leadBidder: 22,
  leadBid: 15,
  leadValue: 16,
  bestPrebid: 15,
  bestPostBid: 15,
  otherBids: 34,
};

export const exportLotsListingToXLSX = ({ eventData, lots, zone }) => {
  const report = buildAuctionReport({ eventData, lots, zone });
  const columns = report.lotColumns;

  const headers = columns.map(({ label }) =>
    withStyle(textCell(label), TABLE_HEADER_STYLE)
  );
  const lotRows = report.lotRows.map((row) =>
    columns.map((column) => lotCell(column, row, report))
  );

  const totalRow = columns.map(({ key }) => {
    if (key === "description") return withStyle(textCell("Total"), TOTAL_STYLE);
    if (key === "leadValue") {
      return withStyle(amountCell(report.totalLeadValue), TOTAL_STYLE);
    }
    return textCell("");
  });

  const worksheet = buildSheet({
    report,
    columnWidths: columns.map(({ key }) => LOT_COLUMN_WIDTHS[key]),
    tableRows: [headers, ...lotRows, totalRow],
  });

  saveWorkbook(worksheet, "Lots", `${report.fileBase}-lots.xlsx`);
};
